import type { ReactNode } from "react";
import type { NavGroup, NavItem, RailUser, Workspace } from "./types";
import { NavIcons } from "./icons";
import { WorkspaceSwitcher } from "./WorkspaceSwitcher";
import { useNavCollapse } from "./useNavCollapse";

/**
 * OperatorRail
 * ─────────────────────────────────────────────────────────────
 * The full labeled rail used by `console.rolloverrep.com`.
 *
 * Two states (persisted to localStorage):
 *   - Expanded (240px) — group labels + item labels + counts visible
 *   - Collapsed (64px) — icons only with hover tooltips, much like
 *                        the customer CRM rail
 *
 * Both states share the same WorkspaceSwitcher up top. The collapse
 * affordance lives next to the user footer at the bottom so it
 * doesn't compete with primary nav.
 */

export interface OperatorRailProps {
  workspaces: Workspace[];
  activeWorkspaceId: string;
  groups: NavGroup[];
  activeId?: string;
  user?: RailUser;
  /** Initial collapsed state. Persisted overrides this after first load. */
  defaultCollapsed?: boolean;
  /** Optional localStorage key — defaults to "rr.nav.operator.collapsed". */
  storageKey?: string;
  onNavigate?: (item: NavItem) => void;
  onSwitchWorkspace?: (workspaceId: string) => void;
  onUserMenu?: () => void;
}

export function OperatorRail({
  workspaces,
  activeWorkspaceId,
  groups,
  activeId,
  user,
  defaultCollapsed = false,
  storageKey = "rr.nav.operator.collapsed",
  onNavigate,
  onSwitchWorkspace,
  onUserMenu,
}: OperatorRailProps) {
  const { collapsed, toggle } = useNavCollapse(storageKey, defaultCollapsed);

  return (
    <aside
      className={"rr-rail rr-rail--ops" + (collapsed ? " is-collapsed" : "")}
      aria-label="Operator Console navigation"
      data-collapsed={collapsed || undefined}
    >
      <div className="rr-rail-head">
        <WorkspaceSwitcher
          workspaces={workspaces}
          activeId={activeWorkspaceId}
          variant={collapsed ? "rail-slim" : "rail-wide"}
          onSelect={onSwitchWorkspace}
        />
      </div>

      <div className="rr-rail-body">
        {groups.map((g) => (
          <div key={g.group} className="rr-rail-group">
            {!collapsed && <div className="rr-rail-group-label">{g.group}</div>}
            {g.items.filter((i) => !i.hidden).map((item) => (
              <OpsRailItem
                key={item.id}
                item={item}
                active={item.id === activeId}
                collapsed={collapsed}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        ))}
      </div>

      <div className="rr-rail-foot">
        {user && (
          <button
            type="button"
            className="rr-rail-user"
            onClick={onUserMenu}
            aria-label={`Account menu — ${user.name}`}
          >
            <span
              className={"rr-rail-avatar rr-rail-avatar--" + (user.avatarColor ?? "teal")}
              aria-hidden="true"
            >
              {user.initials}
            </span>
            {!collapsed && (
              <span className="rr-rail-user-text">
                <span className="rr-rail-user-name">{user.name}</span>
                <span className="rr-rail-user-role">{user.role}</span>
              </span>
            )}
          </button>
        )}
        <button
          type="button"
          className="rr-rail-collapse"
          onClick={toggle}
          aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
          aria-pressed={collapsed}
        >
          <span className="rr-rail-collapse-icon" aria-hidden="true">
            {collapsed ? NavIcons.chevronRight : NavIcons.chevronLeft}
          </span>
          {!collapsed && <span className="rr-rail-collapse-label">Collapse</span>}
        </button>
      </div>
    </aside>
  );
}

function OpsRailItem({
  item,
  active,
  collapsed,
  onNavigate,
}: {
  item: NavItem;
  active: boolean;
  collapsed: boolean;
  onNavigate?: (i: NavItem) => void;
}) {
  return (
    <button
      type="button"
      className={"rr-rail-link" + (active ? " is-active" : "")}
      aria-current={active ? "page" : undefined}
      onClick={() => onNavigate?.(item)}
    >
      <span className="rr-rail-link-icon">{renderIcon(item)}</span>
      {!collapsed && <span className="rr-rail-link-label">{item.label}</span>}
      {!collapsed && item.count != null && <span className="rr-rail-link-count">{item.count}</span>}
      {collapsed && <span className="rr-rail-tip">{item.label}</span>}
    </button>
  );
}

function renderIcon(item: NavItem): ReactNode {
  if (item.icon) return item.icon;
  if (item.iconPath) {
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d={item.iconPath} />
      </svg>
    );
  }
  return (NavIcons as Record<string, ReactNode>)[item.id] ?? null;
}
